import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { useToast } from '../../context/ToastContext';
import { User, Lock, HelpCircle, Trash2, LogOut, ChevronLeft, Check, Send, Eye, EyeOff, AlertTriangle, ArrowLeft, Ticket, Clock, CheckCircle2, XCircle, MessageCircle, List, Paperclip, Image, X, FileText, ShieldAlert, MapPin } from 'lucide-react';
import GoogleMapPicker from '../../components/GoogleMapPicker';
import { validateFiles, scanMessageText } from '../../../lib/security/fileSecurity';
import CustomerSelect from '../../components/CustomerSelect';

const CAT_LABELS = {
  general: 'استفسار عام', order: 'مشكلة في طلب',
  payment: 'مشكلة في الدفع', account: 'مشكلة في الحساب', other: 'أخرى',
};

const renderText = (text) => {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s<>"']+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) =>
    urlRegex.test(part)
      ? <a key={i} href={part} target="_blank" rel="noopener noreferrer" style={{ color:'#38bdf8', textDecoration:'underline', wordBreak:'break-all' }}>{part}</a>
      : part
  );
};

function TicketDetailView({ ticket, apiCall, toast, onReload, T, inputSt, getStatus }) {
  const sm   = getStatus(ticket.status);
  const msgs = ticket.messages || [];
  const canReply = ticket.status !== 'closed';

  const [replyText,    setReplyText]    = useState('');
  const [attachFiles,  setAttachFiles]  = useState([]);
  const [sending,      setSending]      = useState(false);
  const bottomRef  = React.useRef(null);
  const msgsAreaRef = React.useRef(null);

  React.useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [msgs.length]);

  React.useEffect(() => {
    const interval = setInterval(() => { onReload(); }, 2000);
    return () => clearInterval(interval);
  }, [ticket.id]);

  const handleFiles = async e => {
    const selected = Array.from(e.target.files || []);
    e.target.value = '';
    const check = await validateFiles(selected);
    if (!check.safe) { toast.error(check.reason); return; }
    setAttachFiles(p => [...p, ...selected].slice(0, 5));
  };
  const removeFile = idx => setAttachFiles(p => p.filter((_, i) => i !== idx));

  const sendReply = async () => {
    if (!replyText.trim() && attachFiles.length === 0) return;
    const textCheck = scanMessageText(replyText);
    if (!textCheck.safe) { toast.error(textCheck.reason); return; }
    if (textCheck.warning) { toast.warning?.(textCheck.warning) || toast.error(textCheck.warning); }
    if (attachFiles.length > 0) {
      const fileCheck = await validateFiles(attachFiles);
      if (!fileCheck.safe) { toast.error(fileCheck.reason); return; }
    }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('message', replyText.trim() || '...');
      attachFiles.forEach(f => fd.append('files', f));
      const API = process.env.REACT_APP_API_URL || 'http://localhost:8002/api/v1';
      const token = localStorage.getItem('watrly_customer_token');
      const res = await fetch(`${API}/customer/auth/tickets/${ticket.id}/reply`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) { const e = await res.json().catch(()=>({})); throw new Error(e.detail || 'خطأ'); }
      setReplyText('');
      setAttachFiles([]);
      await onReload();
      toast.success('تم إرسال ردك');
    } catch (err) { toast.error(err.message); }
    finally { setSending(false); }
  };

  const fmtDate = d => {
    if (!d) return '';
    const date = new Date(d);
    const now  = new Date();
    const isToday    = date.toDateString() === now.toDateString();
    const yesterday  = new Date(now); yesterday.setDate(now.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();
    const time = date.toLocaleTimeString('ar-SA', { hour:'2-digit', minute:'2-digit' });
    if (isToday)     return `اليوم ${time}`;
    if (isYesterday) return `أمس ${time}`;
    return date.toLocaleString('ar-SA', { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' });
  };
  const isImg   = type => type && type.startsWith('image/');

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem', animation:'pr-in 0.25s ease' }}>

      {/* Header */}
      <div style={{ borderRadius:'1.1rem', overflow:'hidden', background:'var(--wt-surface)', border:`1px solid ${sm.color}30` }}>
        <div style={{ height:3, background:`linear-gradient(90deg,transparent,${sm.color} 40%,rgba(56,189,248,0.5),transparent)` }}/>
        <div style={{ padding:'1rem' }}>
          <div style={{ display:'flex', alignItems:'flex-start', gap:'0.75rem' }}>
            <div style={{ width:40, height:40, borderRadius:'0.875rem', background: sm.bg, border:`1px solid ${sm.color}40`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <Ticket size={18} color={sm.color} strokeWidth={1.8}/>
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:'0.92rem', fontWeight:900, color: T.text, lineHeight:1.4 }}>{ticket.subject}</div>
              <div style={{ fontSize:'0.67rem', color: T.muted, marginTop:'0.2rem', display:'flex', gap:'0.5rem', alignItems:'center', flexWrap:'wrap' }}>
                <span>{ticket.ticket_number}</span>
                {ticket.category && (
                  <span style={{ background:'rgba(56,189,248,0.1)', border:'1px solid rgba(56,189,248,0.2)', color:'#38bdf8', borderRadius:999, padding:'0.1rem 0.5rem', fontSize:'0.62rem', fontWeight:800 }}>
                    {CAT_LABELS[ticket.category] || ticket.category_label || ticket.category}
                  </span>
                )}
              </div>
            </div>
            <span style={{ fontSize:'0.65rem', fontWeight:800, color: sm.color, background: sm.bg, borderRadius:999, padding:'0.2rem 0.6rem', display:'flex', alignItems:'center', gap:'0.25rem', flexShrink:0, border:`1px solid ${sm.color}30` }}>
              {sm.icon}{sm.label}
            </span>
          </div>
        </div>
      </div>

      {/* Chat messages */}
      <div ref={msgsAreaRef} style={{ display:'flex', flexDirection:'column', gap:'0.5rem', padding:'0.25rem 0.1rem' }}>
        {msgs.length === 0 && (
          <div style={{ textAlign:'center', padding:'1.5rem', color: T.muted, fontSize:'0.78rem', borderRadius:'0.875rem', background:'var(--wt-surface)', border:'1px solid var(--wt-border)' }}>
            <MessageCircle size={22} style={{ display:'block', margin:'0 auto 0.5rem', opacity:0.35 }}/>
            لا توجد رسائل بعد
          </div>
        )}
        {msgs.map((msg, i) => {
          const isMine = msg.sender_type === 'customer';
          const atts   = msg.attachments || [];
          return (
            <div key={i} style={{ display:'flex', flexDirection:'column', alignItems: isMine ? 'flex-end' : 'flex-start', animation:`pr-in 0.25s ease ${i*0.04}s both` }}>
              <div style={{
                maxWidth:'84%', borderRadius: isMine ? '1.1rem 1.1rem 0.3rem 1.1rem' : '1.1rem 1.1rem 1.1rem 0.3rem',
                background: isMine ? 'linear-gradient(135deg,rgba(10,186,181,0.15),rgba(8,145,178,0.08))' : 'var(--wt-surface)',
                border: isMine ? '1px solid rgba(10,186,181,0.28)' : '1px solid var(--wt-border)',
                padding:'0.6rem 0.85rem',
              }}>
                <div style={{ fontSize:'0.6rem', fontWeight:800, color: isMine ? '#0ABAB5' : '#38bdf8', marginBottom:'0.28rem' }}>
                  {isMine ? 'أنت' : 'فريق الدعم'}
                </div>
                {msg.message && <div style={{ fontSize:'0.82rem', color: T.text, lineHeight:1.65, wordBreak:'break-word' }}>{renderText(msg.message)}</div>}
                {atts.length > 0 && (
                  <div style={{ display:'flex', flexDirection:'column', gap:'0.4rem', marginTop:'0.5rem' }}>
                    {atts.map((att, ai) => (
                      isImg(att.type)
                        ? <div key={ai} style={{ position:'relative', display:'inline-block', cursor:'zoom-in' }} onClick={() => {
                            const w = window.open(''); w.document.write(`<html><body style="margin:0;background:#000;display:flex;justify-content:center;align-items:center;min-height:100vh"><img src="${att.data_url}" style="max-width:100%;max-height:100vh;object-fit:contain"/></body></html>`);
                          }}>
                            <img src={att.data_url} alt={att.name} style={{ maxWidth:'100%', maxHeight:200, borderRadius:'0.65rem', objectFit:'cover', display:'block', border:'1px solid rgba(255,255,255,0.08)' }}/>
                            <div style={{ position:'absolute', bottom:4, right:4, background:'rgba(0,0,0,0.5)', borderRadius:4, padding:'1px 5px', fontSize:'0.58rem', color:'#fff' }}>🔍</div>
                          </div>
                        : att.type === 'application/pdf'
                          ? <div key={ai} style={{ borderRadius:'0.65rem', overflow:'hidden', border:'1px solid rgba(56,189,248,0.2)' }}>
                              <div style={{ display:'flex', alignItems:'center', gap:'0.4rem', padding:'0.35rem 0.6rem', background:'rgba(56,189,248,0.06)', fontSize:'0.7rem', color:'#38bdf8', fontWeight:700 }}>
                                <FileText size={13}/>{att.name}
                                <a href={att.data_url} download={att.name} style={{ marginRight:'auto', color:'#38bdf8', fontSize:'0.65rem' }}>تحميل</a>
                              </div>
                              <iframe src={att.data_url} title={att.name} style={{ width:'100%', height:180, border:'none', background:'#fff' }}/>
                            </div>
                          : <a key={ai} href={att.data_url} download={att.name} style={{ display:'flex', alignItems:'center', gap:'0.3rem', fontSize:'0.7rem', color:'#38bdf8', background:'rgba(56,189,248,0.08)', border:'1px solid rgba(56,189,248,0.2)', borderRadius:'0.5rem', padding:'0.3rem 0.55rem', textDecoration:'none' }}>
                              <FileText size={12}/>{att.name}
                            </a>
                    ))}
                  </div>
                )}
                <div style={{ fontSize:'0.58rem', color: T.muted, marginTop:'0.3rem', textAlign: isMine ? 'left' : 'right' }}>{fmtDate(msg.created_at)}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div ref={bottomRef} />

      {/* Reply box */}
      {canReply && (
        <div style={{ borderRadius:'1.1rem', background:'var(--wt-surface)', border:'1px solid var(--wt-border)', overflow:'hidden' }}>
          {/* Attached files preview */}
          {attachFiles.length > 0 && (
            <div style={{ padding:'0.6rem 0.75rem 0', display:'flex', flexWrap:'wrap', gap:'0.35rem' }}>
              {attachFiles.map((f, i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:'0.3rem', background:'rgba(56,189,248,0.08)', border:'1px solid rgba(56,189,248,0.2)', borderRadius:'0.5rem', padding:'0.25rem 0.5rem', fontSize:'0.68rem', color:'#38bdf8' }}>
                  {f.type.startsWith('image/') ? <Image size={11}/> : <FileText size={11}/>}
                  <span style={{ maxWidth:80, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{f.name}</span>
                  <button onClick={() => removeFile(i)} style={{ background:'none', border:'none', cursor:'pointer', color:'#94a3b8', padding:0, display:'flex', alignItems:'center' }}><X size={11}/></button>
                </div>
              ))}
            </div>
          )}
          <div style={{ display:'flex', alignItems:'flex-end', gap:'0.5rem', padding:'0.65rem 0.75rem' }}>
            <label style={{ cursor:'pointer', color: T.muted, flexShrink:0, display:'flex', alignItems:'center', paddingBottom:'0.1rem' }}>
              <Paperclip size={18}/>
              <input type="file" multiple accept="image/*,.pdf,.doc,.docx,.txt" onChange={handleFiles} style={{ display:'none' }}/>
            </label>
            <textarea
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              onKeyDown={e => { if (e.key==='Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
              placeholder="اكتب ردك هنا..."
              rows={2}
              style={{ ...inputSt, flex:1, resize:'none', minHeight:42, padding:'0.55rem 0.75rem', fontSize:'0.85rem' }}
            />
            <button onClick={sendReply} disabled={sending || (!replyText.trim() && attachFiles.length===0)} style={{
              width:42, height:42, borderRadius:'0.875rem', border:'none', cursor: sending ? 'not-allowed' : 'pointer', flexShrink:0,
              background: (sending || (!replyText.trim() && attachFiles.length===0)) ? 'rgba(10,186,181,0.25)' : 'linear-gradient(135deg,#0ABAB5,#0891b2)',
              display:'flex', alignItems:'center', justifyContent:'center',
              transition:'background 0.2s',
            }}>
              {sending ? <span style={{ width:14, height:14, borderRadius:'50%', border:'2px solid rgba(255,255,255,0.3)', borderTopColor:'#fff', animation:'pr-spin 0.7s linear infinite', display:'inline-block' }}/> : <Send size={16} color="#fff"/>}
            </button>
          </div>
        </div>
      )}
      {!canReply && (
        <div style={{ textAlign:'center', fontSize:'0.75rem', color: T.muted, padding:'0.5rem' }}>هذه التذكرة مغلقة ولا يمكن إرسال رسائل إليها</div>
      )}
    </div>
  );
}

const APP_VERSION = '1.0.0';

export default function ProfilePage() {
  const { customer, apiCall, logout, refreshCustomer } = useCustomerAuth();
  const toast    = useToast();
  const navigate = useNavigate();

  const [view, setView] = useState('menu');
  const [form, setForm] = useState({ display_name: customer?.display_name || '', email: customer?.email || '' });
  const [pw,   setPw]   = useState({ current_password: '', new_password: '', confirm: '' });
  const [supportForm, setSupportForm] = useState({ subject: '', message: '', category: 'general' });
  const [deleteForm,  setDeleteForm]  = useState({ password: '' });
  const [savingInfo,    setSavingInfo]    = useState(false);
  const [savingPw,      setSavingPw]      = useState(false);
  const [savingSupport, setSavingSupport] = useState(false);
  const [deletingAcct,  setDeletingAcct]  = useState(false);
  const [showPw,  setShowPw]  = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [myTickets,     setMyTickets]     = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  /* ── Addresses state ── */
  const EMPTY_ADDR = { name:'', formatted_address:'', delivery_instructions:'', latitude:null, longitude:null, is_default:false };
  const [sites,       setSites]       = useState([]);
  const [loadingSites,setLoadingSites] = useState(false);
  const [addrModal,   setAddrModal]   = useState(false);
  const [editSite,    setEditSite]    = useState(null);
  const [addrForm,    setAddrForm]    = useState(EMPTY_ADDR);
  const [savingAddr,  setSavingAddr]  = useState(false);
  const [deletingAddr,setDeletingAddr]= useState(null);

  const loadSites = async () => {
    setLoadingSites(true);
    try { setSites(await apiCall('/customer/sites')); }
    catch { /* silent */ }
    finally { setLoadingSites(false); }
  };

  const openAddAddr  = () => { setAddrForm(EMPTY_ADDR); setEditSite(null); setAddrModal(true); };
  const openEditAddr = s  => {
    setAddrForm({ name: s.name, formatted_address: s.formatted_address || s.address_line1 || '',
      delivery_instructions: s.delivery_instructions || '',
      latitude: s.latitude || null, longitude: s.longitude || null, is_default: s.is_default });
    setEditSite(s); setAddrModal(true);
  };

  const saveAddr = async e => {
    e.preventDefault();
    if (!addrForm.name.trim()) { toast.error('أدخل اسم الموقع'); return; }
    if (!addrForm.formatted_address.trim()) { toast.error('اختر الموقع على الخريطة'); return; }
    setSavingAddr(true);
    try {
      const payload = { name: addrForm.name, formatted_address: addrForm.formatted_address,
        delivery_instructions: addrForm.delivery_instructions || null,
        latitude: addrForm.latitude, longitude: addrForm.longitude, is_default: addrForm.is_default };
      if (editSite) {
        await apiCall(`/customer/sites/${editSite.id}`, { method:'PUT', body: JSON.stringify(payload) });
        toast.success('تم تحديث العنوان');
      } else {
        await apiCall('/customer/sites', { method:'POST', body: JSON.stringify(payload) });
        toast.success('تم إضافة العنوان');
      }
      setAddrModal(false);
      loadSites();
    } catch (err) { toast.error(err.message); }
    finally { setSavingAddr(false); }
  };

  const deleteAddr = async id => {
    if (!window.confirm('هل تريد حذف هذا العنوان؟')) return;
    setDeletingAddr(id);
    try { await apiCall(`/customer/sites/${id}`, { method:'DELETE' }); loadSites(); toast.success('تم الحذف'); }
    catch (err) { toast.error(err.message); }
    finally { setDeletingAddr(null); }
  };

  const setAddrDefault = async id => {
    try { await apiCall(`/customer/sites/${id}/default`, { method:'PUT' }); loadSites(); }
    catch (err) { toast.error(err.message); }
  };

  const setF   = k => e => setForm(p => ({ ...p, [k]: e.target.value }));
  const setPwF = k => e => setPw(p => ({ ...p, [k]: e.target.value }));

  const saveInfo = async e => {
    e.preventDefault();
    if (!form.display_name.trim()) { toast.error('أدخل الاسم'); return; }
    setSavingInfo(true);
    try {
      await apiCall('/customer/auth/profile', { method: 'PUT', body: JSON.stringify({ display_name: form.display_name, email: form.email || null }) });
      await refreshCustomer();
      toast.success('تم تحديث المعلومات');
      setView('menu');
    } catch (err) { toast.error(err.message); }
    finally { setSavingInfo(false); }
  };

  const savePassword = async e => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) { toast.error('كلمتا المرور غير متطابقتين'); return; }
    if (pw.new_password.length < 6) { toast.error('كلمة المرور قصيرة جداً'); return; }
    setSavingPw(true);
    try {
      await apiCall('/customer/auth/password', { method: 'PUT', body: JSON.stringify({ current_password: pw.current_password, new_password: pw.new_password }) });
      toast.success('تم تغيير كلمة المرور');
      setPw({ current_password: '', new_password: '', confirm: '' });
      setView('menu');
    } catch (err) { toast.error(err.message); }
    finally { setSavingPw(false); }
  };

  const sendSupport = async e => {
    e.preventDefault();
    if (!supportForm.subject.trim() || !supportForm.message.trim()) { toast.error('يرجى ملء جميع الحقول'); return; }
    setSavingSupport(true);
    try {
      await apiCall('/customer/auth/support', { method: 'POST', body: JSON.stringify(supportForm) });
      toast.success('تم إرسال رسالتك، سنرد عليك قريباً');
      setSupportForm({ subject: '', message: '', category: 'general' });
      await loadMyTickets();
      setView('support');
    } catch (err) { toast.error(err.message); }
    finally { setSavingSupport(false); }
  };

  const deleteAccount = async e => {
    e.preventDefault();
    if (!deleteForm.password) { toast.error('أدخل كلمة المرور'); return; }
    setDeletingAcct(true);
    try {
      await apiCall('/customer/auth/account', { method: 'DELETE', body: JSON.stringify(deleteForm) });
      toast.success('تم حذف حسابك');
      logout();
      navigate('/customer/login', { replace: true });
    } catch (err) { toast.error(err.message); }
    finally { setDeletingAcct(false); }
  };

  const handleLogout = () => { logout(); navigate('/customer/login', { replace: true }); };

  const loadMyTickets = async () => {
    setLoadingTickets(true);
    try {
      const data = await apiCall('/customer/auth/tickets');
      setMyTickets(Array.isArray(data) ? data : (data.items || []));
    } catch { setMyTickets([]); }
    finally { setLoadingTickets(false); }
  };

  const getCustReadCount = (id) => parseInt(localStorage.getItem(`wt_cust_read_${id}`) || '0', 10);
  const setCustReadCount = (id, n) => localStorage.setItem(`wt_cust_read_${id}`, String(n));
  const getCustUnread = (t) => {
    // use agent_message_count from API (list view) or count from messages array (detail view)
    const agentMsgs = t.agent_message_count
      ?? (t.messages ? t.messages.filter(m => m.sender_type !== 'customer').length : 0);
    const seen = getCustReadCount(t.id);
    return Math.max(0, agentMsgs - seen);
  };

  const openTicketDetail = async (ticket, fromView) => {
    try {
      const detail = await apiCall(`/customer/auth/tickets/${ticket.id}`);
      // mark agent messages as read
      const agentMsgs = (detail.messages || []).filter(m => m.sender_type !== 'customer').length;
      setCustReadCount(ticket.id, agentMsgs);
      setSelectedTicket({ ...detail, _from: fromView || view });
      setView('ticket-detail');
    } catch {
      setSelectedTicket({ ...ticket, _from: fromView || view });
      setView('ticket-detail');
    }
  };

  const statusMeta = {
    open:        { label:'مفتوحة',      color:'#f59e0b', bg:'rgba(245,158,11,0.12)',  icon: <Clock size={11}/> },
    in_progress: { label:'قيد المعالجة', color:'#38bdf8', bg:'rgba(56,189,248,0.12)',  icon: <Clock size={11}/> },
    resolved:    { label:'تم الحل',     color:'#22c55e', bg:'rgba(34,197,94,0.12)',   icon: <CheckCircle2 size={11}/> },
    closed:      { label:'مغلقة',       color:'#94a3b8', bg:'rgba(148,163,184,0.12)', icon: <XCircle size={11}/> },
  };
  const getStatus = s => statusMeta[s] || statusMeta['open'];

  useEffect(() => {
    if (view === 'support' || view === 'my-tickets') loadMyTickets();
    if (view === 'addresses') loadSites();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  useEffect(() => {
    if (view !== 'support' && view !== 'my-tickets') return;
    const interval = setInterval(() => {
      if (!document.hidden) loadMyTickets();
    }, 5000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const typeLabel = { personal: 'شخصي', business: 'تجاري', government: 'حكومي' };
  const initials  = (customer?.display_name || 'ع')[0];

  const T = { text: 'var(--wt-text-primary)', muted: 'var(--wt-text-muted)' };

  const inputSt = {
    width:'100%', padding:'0.72rem 0.9rem', borderRadius:'0.75rem', outline:'none',
    background:'rgba(255,255,255,0.05)', border:'1.5px solid var(--wt-border)',
    color: T.text, fontSize:'0.875rem', fontWeight:600,
    fontFamily:"'Cairo',sans-serif", boxSizing:'border-box',
  };

  const btnPrimary = {
    width:'100%', padding:'0.8rem', borderRadius:'0.875rem', border:'none', cursor:'pointer',
    background:'linear-gradient(135deg,#0ABAB5,#0891b2)', color:'#fff',
    fontSize:'0.875rem', fontWeight:800, fontFamily:"'Cairo',sans-serif",
    boxShadow:'0 4px 16px rgba(10,186,181,0.3)',
    display:'flex', alignItems:'center', justifyContent:'center', gap:'0.4rem',
  };

  const backTarget = view === 'ticket-detail' ? (selectedTicket?._from || 'support') : view === 'my-tickets' ? 'support' : 'menu';
  const BackBtn = () => (
    <button onClick={() => setView(backTarget)} style={{
      display:'flex', alignItems:'center', gap:'0.4rem', background:'none', border:'none',
      cursor:'pointer', color:'#0ABAB5', fontWeight:700, fontFamily:"'Cairo',sans-serif", fontSize:'0.82rem', padding:0,
    }}>
      <ArrowLeft size={16}/>
      رجوع
    </button>
  );

  const Spinner = () => <span style={{ width:14, height:14, borderRadius:'50%', border:'2px solid rgba(255,255,255,0.3)', borderTopColor:'#fff', animation:'pr-spin 0.7s linear infinite', display:'inline-block' }}/>;

  return (
    <div dir="rtl" style={{ display:'flex', flexDirection:'column', gap:'1rem', fontFamily:"'Cairo',sans-serif" }}>
      <style>{`
        @keyframes pr-in      { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        @keyframes pr-spin    { to{transform:rotate(360deg)} }
        @keyframes pr-pulse   { 0%,100%{opacity:1} 50%{opacity:0.45} }
        @keyframes pr-shimmer { from{transform:translateX(100%)} to{transform:translateX(-100%)} }
        @keyframes pr-glow    { 0%,100%{box-shadow:0 0 8px rgba(56,189,248,0.25)} 50%{box-shadow:0 0 18px rgba(56,189,248,0.55),0 0 32px rgba(10,186,181,0.2)} }
        @keyframes pr-badge   { 0%,100%{transform:scale(1)} 50%{transform:scale(1.07)} }
        .pr-input:focus { border-color:rgba(10,186,181,0.7) !important; box-shadow:0 0 0 3px rgba(10,186,181,0.12) !important; }
        .pr-input::placeholder { opacity:0.35; }
        .pr-row { transition:opacity 0.15s, background 0.15s; }
        .pr-row:active { opacity:0.75; }
        .pr-hide-scrollbar { scrollbar-width:none; -ms-overflow-style:none; }
        .pr-hide-scrollbar::-webkit-scrollbar { display:none; }
        .pr-ticket-card { transition:transform 0.18s ease, box-shadow 0.18s ease; }
        .pr-ticket-card:hover { transform:translateY(-2px); box-shadow:0 8px 24px rgba(56,189,248,0.18) !important; }
        .pr-ticket-card:active { transform:scale(0.98); }
        .pr-status-pulse { animation: pr-pulse 2s ease-in-out infinite; }
        .pr-card-glow { animation: pr-glow 2.5s ease-in-out infinite; }
      `}</style>

      {/* ══ HERO ══ */}
      <div style={{
        borderRadius:'1.375rem', overflow:'hidden', position:'relative',
        background:'linear-gradient(135deg,#062040 0%,#083060 45%,#0a3d75 100%)',
        boxShadow:'0 8px 32px rgba(0,0,0,0.4), 0 0 0 1px rgba(10,186,181,0.15)',
        animation:'pr-in 0.35s ease',
      }}>
        <div style={{ height:3, background:'linear-gradient(90deg,transparent,#0ABAB5 40%,rgba(56,189,248,0.7),transparent)' }}/>
        <div style={{ padding:'1.25rem', display:'flex', alignItems:'center', gap:'1rem' }}>
          <div style={{
            width:60, height:60, borderRadius:'1rem', flexShrink:0,
            background:'linear-gradient(135deg,rgba(10,186,181,0.25),rgba(8,145,178,0.15))',
            border:'2px solid rgba(10,186,181,0.4)',
            display:'flex', alignItems:'center', justifyContent:'center',
            color:'#0ABAB5', fontWeight:900, fontSize:'1.5rem',
            boxShadow:'0 0 24px rgba(10,186,181,0.25)',
          }}>{initials}</div>
          <div style={{ minWidth:0, flex:1 }}>
            <div style={{ fontSize:'1.1rem', fontWeight:900, color:'#fff', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{customer?.display_name}</div>
            <div style={{ fontSize:'0.75rem', color:'rgba(186,230,253,0.5)', marginTop:'0.15rem', direction:'ltr', textAlign:'right' }}>{customer?.phone}</div>
            <div style={{ display:'flex', gap:'0.4rem', marginTop:'0.45rem', flexWrap:'wrap' }}>
              <span style={{ background:'rgba(10,186,181,0.15)', border:'1px solid rgba(10,186,181,0.3)', color:'#0ABAB5', fontSize:'0.62rem', fontWeight:800, borderRadius:999, padding:'0.12rem 0.55rem' }}>{typeLabel[customer?.customer_type] || 'عميل'}</span>
              {customer?.total_orders > 0 && <span style={{ background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.1)', color:'rgba(255,255,255,0.55)', fontSize:'0.62rem', fontWeight:700, borderRadius:999, padding:'0.12rem 0.55rem' }}>{customer.total_orders} طلب</span>}
            </div>
          </div>
        </div>
      </div>

      {/* ══ BACK BTN (when in sub-view) ══ */}
      {view !== 'menu' && <BackBtn />}

      {/* ══ MENU ══ */}
      {view === 'menu' && (
        <div style={{ display:'flex', flexDirection:'column', gap:'0.625rem', animation:'pr-in 0.25s ease' }}>

          {/* Settings */}
          <div style={{ borderRadius:'1.1rem', overflow:'hidden', background:'var(--wt-surface)', border:'1px solid var(--wt-border)' }}>
            {[
              { key:'info',     label:'المعلومات الشخصية', sub:'الاسم والبريد الإلكتروني', color:'#0ABAB5', icon: <User size={17} color="#0ABAB5" strokeWidth={1.8}/> },
              { key:'password', label:'كلمة المرور', sub:'تغيير كلمة المرور', color:'#0ABAB5', icon: <Lock size={17} color="#0ABAB5" strokeWidth={1.8}/> },
            ].map((item, i, arr) => (
              <button key={item.key} className="pr-row" onClick={() => setView(item.key)} style={{
                width:'100%', display:'flex', alignItems:'center', gap:'0.75rem',
                padding:'0.875rem 1rem', background:'none', border:'none', cursor:'pointer',
                borderBottom: i < arr.length-1 ? '1px solid var(--wt-border)' : 'none',
              }}>
                <div style={{ width:36, height:36, borderRadius:'0.75rem', background:'rgba(10,186,181,0.08)', border:'1px solid rgba(10,186,181,0.15)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>{item.icon}</div>
                <div style={{ flex:1, textAlign:'right' }}>
                  <div style={{ fontSize:'0.875rem', fontWeight:800, color: T.text }}>{item.label}</div>
                  <div style={{ fontSize:'0.68rem', color: T.muted, marginTop:'0.1rem' }}>{item.sub}</div>
                </div>
                <ChevronLeft size={14} style={{ color: T.muted, flexShrink:0 }}/>
              </button>
            ))}
          </div>

          {/* Addresses */}
          <div style={{ borderRadius:'1.1rem', overflow:'hidden', background:'var(--wt-surface)', border:'1px solid var(--wt-border)' }}>
            <button className="pr-row" onClick={() => setView('addresses')} style={{ width:'100%', display:'flex', alignItems:'center', gap:'0.75rem', padding:'0.875rem 1rem', background:'none', border:'none', cursor:'pointer' }}>
              <div style={{ width:36, height:36, borderRadius:'0.75rem', background:'rgba(10,186,181,0.08)', border:'1px solid rgba(10,186,181,0.15)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <MapPin size={17} color="#0ABAB5" strokeWidth={1.8}/>
              </div>
              <div style={{ flex:1, textAlign:'right' }}>
                <div style={{ fontSize:'0.875rem', fontWeight:800, color: T.text }}>عناويني</div>
                <div style={{ fontSize:'0.68rem', color: T.muted, marginTop:'0.1rem' }}>مواقع التوصيل المحفوظة</div>
              </div>
              <ChevronLeft size={14} style={{ color: T.muted, flexShrink:0 }}/>
            </button>
          </div>

          {/* Support */}
          <div style={{ borderRadius:'1.1rem', overflow:'hidden', background:'var(--wt-surface)', border:'1px solid var(--wt-border)' }}>
            <button className="pr-row" onClick={() => setView('support')} style={{ width:'100%', display:'flex', alignItems:'center', gap:'0.75rem', padding:'0.875rem 1rem', background:'none', border:'none', cursor:'pointer' }}>
              <div style={{ width:36, height:36, borderRadius:'0.75rem', background:'rgba(56,189,248,0.08)', border:'1px solid rgba(56,189,248,0.15)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <HelpCircle size={17} color="#38bdf8" strokeWidth={1.8}/>
              </div>
              <div style={{ flex:1, textAlign:'right' }}>
                <div style={{ fontSize:'0.875rem', fontWeight:800, color: T.text }}>التواصل مع الدعم</div>
                <div style={{ fontSize:'0.68rem', color: T.muted, marginTop:'0.1rem' }}>أرسل رسالة لفريق الدعم</div>
              </div>
              <ChevronLeft size={14} style={{ color: T.muted, flexShrink:0 }}/>
            </button>
          </div>

          {/* Info */}
          <div style={{ borderRadius:'1.1rem', overflow:'hidden', background:'var(--wt-surface)', border:'1px solid var(--wt-border)' }}>
            {[
              { label:'إصدار التطبيق', value:`v${APP_VERSION}` },
              { label:'نوع الحساب', value: typeLabel[customer?.customer_type] || '—' },
              customer?.last_login_at && { label:'آخر دخول', value: new Date(customer.last_login_at).toLocaleDateString('ar-SA') },
            ].filter(Boolean).map(({ label, value }, i, arr) => (
              <div key={label} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.75rem 1rem', borderBottom: i < arr.length-1 ? '1px solid var(--wt-border)' : 'none' }}>
                <span style={{ fontSize:'0.8rem', color: T.muted }}>{label}</span>
                <span style={{ fontSize:'0.82rem', fontWeight:700, color: T.text }}>{value}</span>
              </div>
            ))}
          </div>

          {/* Danger */}
          <div style={{ borderRadius:'1.1rem', overflow:'hidden', background:'var(--wt-surface)', border:'1px solid var(--wt-border)' }}>
            <button className="pr-row" onClick={handleLogout} style={{ width:'100%', display:'flex', alignItems:'center', gap:'0.75rem', padding:'0.875rem 1rem', background:'none', border:'none', cursor:'pointer', borderBottom:'1px solid var(--wt-border)' }}>
              <div style={{ width:36, height:36, borderRadius:'0.75rem', background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.15)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <LogOut size={17} color="#f59e0b" strokeWidth={1.8}/>
              </div>
              <div style={{ flex:1, textAlign:'right' }}><div style={{ fontSize:'0.875rem', fontWeight:800, color:'#f59e0b' }}>تسجيل الخروج</div></div>
            </button>
            <button className="pr-row" onClick={() => setView('delete')} style={{ width:'100%', display:'flex', alignItems:'center', gap:'0.75rem', padding:'0.875rem 1rem', background:'none', border:'none', cursor:'pointer' }}>
              <div style={{ width:36, height:36, borderRadius:'0.75rem', background:'rgba(244,63,94,0.08)', border:'1px solid rgba(244,63,94,0.15)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <Trash2 size={17} color="#f43f5e" strokeWidth={1.8}/>
              </div>
              <div style={{ flex:1, textAlign:'right' }}>
                <div style={{ fontSize:'0.875rem', fontWeight:800, color:'#f43f5e' }}>حذف الحساب</div>
                <div style={{ fontSize:'0.68rem', color: T.muted, marginTop:'0.1rem' }}>حذف نهائي لا يمكن التراجع</div>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* ══ ADDRESSES VIEW ══ */}
      {view === 'addresses' && (
        <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem', animation:'pr-in 0.25s ease' }}>
          {/* Header */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <p style={{ fontSize:'0.72rem', color: T.muted, margin:0 }}>
              {loadingSites ? '...' : `${sites.length} عنوان محفوظ`}
            </p>
            <button onClick={openAddAddr} style={{
              display:'flex', alignItems:'center', gap:'0.35rem',
              padding:'0.5rem 0.9rem', borderRadius:'0.75rem', border:'1px solid rgba(10,186,181,0.4)',
              background:'rgba(10,186,181,0.1)', color:'#0ABAB5',
              fontSize:'0.78rem', fontWeight:800, cursor:'pointer', fontFamily:"'Cairo',sans-serif",
            }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 4v16M4 12h16"/></svg>
              إضافة عنوان
            </button>
          </div>

          {/* Loading */}
          {loadingSites && [1,2].map(i => (
            <div key={i} style={{ height:90, borderRadius:'1rem', background:'var(--wt-surface)', border:'1px solid var(--wt-border)', opacity:0.5 }}/>
          ))}

          {/* Empty */}
          {!loadingSites && sites.length === 0 && (
            <div style={{ textAlign:'center', padding:'2.5rem 1rem', borderRadius:'1.1rem', background:'var(--wt-surface)', border:'1px solid var(--wt-border)' }}>
              <MapPin size={28} color="#0ABAB5" style={{ opacity:0.4, display:'block', margin:'0 auto 0.75rem' }}/>
              <p style={{ color: T.text, fontWeight:800, margin:'0 0 0.3rem', fontSize:'0.9rem' }}>لا توجد عناوين</p>
              <p style={{ color: T.muted, fontSize:'0.75rem', margin:'0 0 1rem' }}>أضف موقع توصيلك المفضّل</p>
              <button onClick={openAddAddr} style={{
                padding:'0.65rem 1.5rem', borderRadius:'0.875rem', border:'none', cursor:'pointer',
                background:'linear-gradient(135deg,#0ABAB5,#0891b2)', color:'#fff',
                fontSize:'0.85rem', fontWeight:800, fontFamily:"'Cairo',sans-serif",
                boxShadow:'0 4px 16px rgba(10,186,181,0.3)',
              }}>إضافة عنوان</button>
            </div>
          )}

          {/* Cards */}
          {!loadingSites && sites.map((site, i) => (
            <div key={site.id} style={{
              borderRadius:'1.1rem', overflow:'hidden', background:'var(--wt-surface)',
              border:`1.5px solid ${site.is_default ? 'rgba(10,186,181,0.4)' : 'var(--wt-border)'}`,
              boxShadow: site.is_default ? '0 0 20px rgba(10,186,181,0.1)' : 'none',
              animation:`pr-in 0.25s ease ${i*0.05}s both`,
            }}>
              {site.is_default && <div style={{ height:2.5, background:'linear-gradient(90deg,transparent,#0ABAB5,rgba(56,189,248,0.7),transparent)' }}/>}
              <div style={{ padding:'0.875rem 1rem' }}>
                <div style={{ display:'flex', alignItems:'flex-start', gap:'0.75rem' }}>
                  <div style={{
                    width:40, height:40, borderRadius:'0.875rem', flexShrink:0,
                    background: site.is_default ? 'linear-gradient(135deg,#0ABAB5,#0891b2)' : 'rgba(10,186,181,0.08)',
                    border:`1px solid ${site.is_default ? 'rgba(10,186,181,0.5)' : 'rgba(10,186,181,0.2)'}`,
                    display:'flex', alignItems:'center', justifyContent:'center',
                  }}>
                    <MapPin size={18} color={site.is_default ? '#fff' : '#0ABAB5'} strokeWidth={1.8}/>
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'0.4rem', marginBottom:'0.2rem' }}>
                      <span style={{ fontSize:'0.88rem', fontWeight:800, color: T.text }}>{site.name}</span>
                      {site.is_default && (
                        <span style={{ fontSize:'0.58rem', fontWeight:800, borderRadius:999, padding:'0.1rem 0.5rem', background:'rgba(10,186,181,0.15)', color:'#0ABAB5', border:'1px solid rgba(10,186,181,0.3)' }}>افتراضي</span>
                      )}
                    </div>
                    <p style={{ fontSize:'0.73rem', color: T.muted, margin:0, lineHeight:1.5 }}>
                      {site.formatted_address || site.address_line1}
                    </p>
                    {site.delivery_instructions && (
                      <p style={{ fontSize:'0.68rem', color:'rgba(10,186,181,0.7)', margin:'0.3rem 0 0', fontStyle:'italic' }}>
                        {site.delivery_instructions}
                      </p>
                    )}
                  </div>
                </div>
                <div style={{ display:'flex', gap:'0.5rem', marginTop:'0.75rem', paddingTop:'0.65rem', borderTop:'1px solid var(--wt-border)' }}>
                  {!site.is_default && (
                    <button onClick={() => setAddrDefault(site.id)} style={{
                      flex:1, padding:'0.45rem', borderRadius:'0.6rem', border:'1px solid rgba(10,186,181,0.25)',
                      background:'rgba(10,186,181,0.07)', color:'#0ABAB5',
                      fontSize:'0.65rem', fontWeight:800, cursor:'pointer', fontFamily:"'Cairo',sans-serif",
                    }}>افتراضي</button>
                  )}
                  <button onClick={() => openEditAddr(site)} style={{
                    flex:1, padding:'0.45rem', borderRadius:'0.6rem', border:'1px solid var(--wt-border)',
                    background:'rgba(255,255,255,0.04)', color: T.muted,
                    fontSize:'0.65rem', fontWeight:800, cursor:'pointer', fontFamily:"'Cairo',sans-serif",
                  }}>تعديل</button>
                  <button onClick={() => deleteAddr(site.id)} disabled={deletingAddr===site.id} style={{
                    flex:1, padding:'0.45rem', borderRadius:'0.6rem', border:'1px solid rgba(244,63,94,0.2)',
                    background:'rgba(244,63,94,0.06)', color:'#f43f5e',
                    fontSize:'0.65rem', fontWeight:800, cursor: deletingAddr===site.id?'not-allowed':'pointer',
                    opacity: deletingAddr===site.id?0.5:1, fontFamily:"'Cairo',sans-serif",
                  }}>
                    {deletingAddr===site.id ? '...' : 'حذف'}
                  </button>
                </div>
              </div>
            </div>
          ))}

          {/* ── Add/Edit Modal ── */}
          {addrModal && (
            <div style={{
              position:'fixed', inset:0, zIndex:9999,
              display:'flex', alignItems:'flex-end',
              background:'rgba(0,0,0,0.65)', backdropFilter:'blur(6px)',
            }} dir="rtl" onClick={() => setAddrModal(false)}>
              <div style={{
                width:'100%', maxHeight:'95vh', overflowY:'auto',
                background:'var(--wt-surface, #0b1e30)',
                borderRadius:'1.375rem 1.375rem 0 0',
                boxShadow:'0 -16px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(10,186,181,0.12)',
                animation:'pr-in 0.3s cubic-bezier(0.34,1.2,0.64,1)',
              }} onClick={e => e.stopPropagation()}>
                {/* Handle */}
                <div style={{ display:'flex', justifyContent:'center', padding:'0.75rem 0 0.25rem' }}>
                  <div style={{ width:36, height:4, borderRadius:999, background:'rgba(255,255,255,0.12)' }}/>
                </div>
                <div style={{ height:2, background:'linear-gradient(90deg,transparent,#0ABAB5 40%,rgba(56,189,248,0.7),transparent)', margin:'0 1rem', borderRadius:999 }}/>

                {/* Header */}
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.875rem 1.1rem 0.5rem' }}>
                  <div>
                    <div style={{ fontSize:'0.6rem', color:'rgba(10,186,181,0.6)', fontWeight:700, letterSpacing:'0.1em' }}>WTRLY</div>
                    <div style={{ fontSize:'1rem', fontWeight:900, color: T.text }}>
                      {editSite ? 'تعديل العنوان' : 'إضافة عنوان جديد'}
                    </div>
                  </div>
                  <button onClick={() => setAddrModal(false)} style={{
                    width:32, height:32, borderRadius:'50%', border:'none', cursor:'pointer',
                    background:'rgba(255,255,255,0.07)', color: T.muted,
                    display:'flex', alignItems:'center', justifyContent:'center',
                  }}>✕</button>
                </div>

                {/* Form */}
                <form onSubmit={saveAddr} style={{ padding:'0.5rem 1.1rem 2.5rem', display:'flex', flexDirection:'column', gap:'0.875rem' }}>

                  {/* Name */}
                  <div>
                    <label style={{ display:'block', fontSize:'0.7rem', fontWeight:700, color: T.muted, marginBottom:'0.35rem' }}>
                      اسم الموقع <span style={{ color:'#f43f5e' }}>*</span>
                    </label>
                    <input
                      className="pr-input" type="text"
                      value={addrForm.name}
                      onChange={e => setAddrForm(p => ({ ...p, name: e.target.value }))}
                      placeholder="مثال: المنزل، المكتب..."
                      style={inputSt}
                    />
                  </div>

                  {/* Google Map */}
                  <div>
                    <label style={{ display:'block', fontSize:'0.7rem', fontWeight:700, color: T.muted, marginBottom:'0.35rem' }}>
                      الموقع على الخريطة <span style={{ color:'#f43f5e' }}>*</span>
                    </label>
                    <GoogleMapPicker
                      initialPos={addrForm.latitude && addrForm.longitude ? [addrForm.latitude, addrForm.longitude] : null}
                      onPick={({ lat, lng, address }) => setAddrForm(p => ({ ...p, latitude: lat, longitude: lng, formatted_address: address }))}
                      height={260}
                    />
                    {addrForm.formatted_address ? (
                      <div style={{ marginTop:'0.5rem', padding:'0.5rem 0.75rem', borderRadius:'0.65rem', background:'rgba(10,186,181,0.06)', border:'1px solid rgba(10,186,181,0.2)', fontSize:'0.72rem', color:'#0ABAB5', fontWeight:700 }}>
                        📍 {addrForm.formatted_address}
                      </div>
                    ) : (
                      <p style={{ fontSize:'0.68rem', color: T.muted, marginTop:'0.4rem', opacity:0.7 }}>ابحث عن موقع أو انقر على الخريطة مباشرة</p>
                    )}
                  </div>

                  {/* Delivery instructions */}
                  <div>
                    <label style={{ display:'block', fontSize:'0.7rem', fontWeight:700, color: T.muted, marginBottom:'0.35rem' }}>تعليمات التوصيل (اختياري)</label>
                    <input
                      className="pr-input" type="text"
                      value={addrForm.delivery_instructions}
                      onChange={e => setAddrForm(p => ({ ...p, delivery_instructions: e.target.value }))}
                      placeholder="مثال: اطرق الباب الجانبي..."
                      style={inputSt}
                    />
                  </div>

                  {/* Default toggle */}
                  <div style={{
                    display:'flex', alignItems:'center', justifyContent:'space-between',
                    padding:'0.65rem 0.875rem', borderRadius:'0.75rem',
                    background:'rgba(10,186,181,0.06)', border:'1px solid rgba(10,186,181,0.15)', cursor:'pointer',
                  }} onClick={() => setAddrForm(p => ({ ...p, is_default: !p.is_default }))}>
                    <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0ABAB5" strokeWidth="2" strokeLinecap="round"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                      <span style={{ fontSize:'0.8rem', fontWeight:700, color: T.text }}>تعيين كعنوان افتراضي</span>
                    </div>
                    <div style={{
                      width:40, height:22, borderRadius:999, position:'relative',
                      background: addrForm.is_default ? '#0ABAB5' : 'rgba(255,255,255,0.1)',
                      border:`1.5px solid ${addrForm.is_default ? '#0ABAB5' : 'rgba(255,255,255,0.15)'}`,
                      transition:'background 0.2s',
                    }}>
                      <div style={{
                        position:'absolute', top:2, width:14, height:14, borderRadius:'50%', background:'#fff',
                        left: addrForm.is_default ? 22 : 2, transition:'left 0.2s',
                        boxShadow:'0 1px 4px rgba(0,0,0,0.3)',
                      }}/>
                    </div>
                  </div>

                  {/* Buttons */}
                  <div style={{ display:'flex', gap:'0.625rem' }}>
                    <button type="button" onClick={() => setAddrModal(false)} style={{
                      flex:1, padding:'0.8rem', borderRadius:'0.875rem', border:'1px solid var(--wt-border)',
                      background:'rgba(255,255,255,0.05)', color: T.muted,
                      fontSize:'0.875rem', fontWeight:700, cursor:'pointer', fontFamily:"'Cairo',sans-serif",
                    }}>إلغاء</button>
                    <button type="submit" disabled={savingAddr} style={{
                      flex:2, padding:'0.8rem', borderRadius:'0.875rem', border:'none',
                      cursor: savingAddr ? 'not-allowed' : 'pointer',
                      background:'linear-gradient(135deg,#0ABAB5,#0891b2)', color:'#fff',
                      fontSize:'0.875rem', fontWeight:800, fontFamily:"'Cairo',sans-serif",
                      boxShadow:'0 4px 16px rgba(10,186,181,0.3)', opacity: savingAddr ? 0.65 : 1,
                      display:'flex', alignItems:'center', justifyContent:'center', gap:'0.4rem',
                    }}>
                      {savingAddr
                        ? <><Spinner/> جاري الحفظ...</>
                        : <><Check size={14}/> حفظ العنوان</>
                      }
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══ INFO VIEW ══ */}
      {view === 'info' && (
        <div style={{ borderRadius:'1.1rem', padding:'1.25rem', background:'var(--wt-surface)', border:'1px solid var(--wt-border)', animation:'pr-in 0.25s ease' }}>
          <form onSubmit={saveInfo} style={{ display:'flex', flexDirection:'column', gap:'0.875rem' }}>
            {[
              { label:'الاسم الكامل', key:'display_name', type:'text', placeholder:'اسمك الكامل', required:true },
              { label:'البريد الإلكتروني', key:'email', type:'email', placeholder:'example@email.com', dir:'ltr' },
            ].map(f => (
              <div key={f.key}>
                <label style={{ display:'block', fontSize:'0.7rem', fontWeight:700, color: T.muted, marginBottom:'0.35rem' }}>{f.label} {f.required && <span style={{ color:'#f43f5e' }}>*</span>}</label>
                <input className="pr-input" type={f.type} value={form[f.key]} onChange={setF(f.key)} placeholder={f.placeholder} dir={f.dir} required={f.required} style={inputSt}/>
              </div>
            ))}
            <div>
              <label style={{ display:'block', fontSize:'0.7rem', fontWeight:700, color: T.muted, marginBottom:'0.35rem' }}>رقم الهاتف</label>
              <input type="tel" value={customer?.phone || ''} disabled dir="ltr" style={{ ...inputSt, opacity:0.4, cursor:'not-allowed' }}/>
              <p style={{ fontSize:'0.65rem', color: T.muted, marginTop:'0.3rem', opacity:0.7 }}>لا يمكن تغيير رقم الهاتف</p>
            </div>
            <button type="submit" disabled={savingInfo} style={{ ...btnPrimary, opacity: savingInfo ? 0.65 : 1, cursor: savingInfo ? 'not-allowed' : 'pointer' }}>
              {savingInfo ? <><Spinner/> جاري الحفظ...</> : <><Check size={14}/> حفظ التغييرات</>}
            </button>
          </form>
        </div>
      )}

      {/* ══ PASSWORD VIEW ══ */}
      {view === 'password' && (
        <div style={{ borderRadius:'1.1rem', padding:'1.25rem', background:'var(--wt-surface)', border:'1px solid var(--wt-border)', animation:'pr-in 0.25s ease' }}>
          <form onSubmit={savePassword} style={{ display:'flex', flexDirection:'column', gap:'0.875rem' }}>
            {[
              { label:'كلمة المرور الحالية', key:'current_password', showState: showPw,  toggle:() => setShowPw(p=>!p) },
              { label:'كلمة المرور الجديدة', key:'new_password',      showState: showPw2, toggle:() => setShowPw2(p=>!p) },
              { label:'تأكيد كلمة المرور',   key:'confirm',           showState: showPw2, toggle: null },
            ].map(f => (
              <div key={f.key}>
                <label style={{ display:'block', fontSize:'0.7rem', fontWeight:700, color: T.muted, marginBottom:'0.35rem' }}>{f.label}</label>
                <div style={{ position:'relative' }}>
                  <input className="pr-input" type={f.showState ? 'text' : 'password'} value={pw[f.key]} onChange={setPwF(f.key)} placeholder="••••••••" required dir="ltr"
                    style={{ ...inputSt, paddingLeft: f.toggle ? '2.5rem' : inputSt.padding }}/>
                  {f.toggle && (
                    <button type="button" onClick={f.toggle} style={{ position:'absolute', left:'0.75rem', top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color: T.muted }}>
                      {f.showState ? <EyeOff size={15}/> : <Eye size={15}/>}
                    </button>
                  )}
                </div>
              </div>
            ))}
            <button type="submit" disabled={savingPw} style={{ ...btnPrimary, opacity: savingPw ? 0.65 : 1, cursor: savingPw ? 'not-allowed' : 'pointer' }}>
              {savingPw ? <><Spinner/> جاري التغيير...</> : <><Lock size={14}/> تغيير كلمة المرور</>}
            </button>
          </form>
        </div>
      )}

      {/* ══ SUPPORT VIEW ══ */}
      {view === 'support' && (
        <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem', animation:'pr-in 0.25s ease' }}>

          {/* Active tickets — horizontal swipeable */}
          {(() => {
            const active = myTickets.filter(t => !['resolved','closed'].includes(t.status));
            if (active.length === 0) return null;
            return (
              <div style={{ display:'flex', flexDirection:'column', gap:'0.4rem' }}>
                {/* Header row with count badge */}
                <div style={{ display:'flex', alignItems:'center', gap:'0.4rem', paddingRight:'0.25rem' }}>
                  <span className="pr-status-pulse" style={{ width:6, height:6, borderRadius:'50%', background:'#38bdf8', flexShrink:0 }}/>
                  <span style={{ fontSize:'0.72rem', fontWeight:700, color: T.muted }}>تذاكرك النشطة مع الدعم</span>
                  <span style={{ fontSize:'0.65rem', fontWeight:900, color:'#38bdf8', background:'rgba(56,189,248,0.12)', border:'1px solid rgba(56,189,248,0.25)', borderRadius:999, padding:'0.1rem 0.5rem', marginRight:'auto' }}>
                    {active.length} {active.length === 1 ? 'تذكرة' : 'تذاكر'}
                  </span>
                </div>
                {/* Horizontal scroll rail */}
                <div style={{ display:'flex', gap:'0.65rem', overflowX:'auto', paddingBottom:'0.35rem', scrollSnapType:'x mandatory', WebkitOverflowScrolling:'touch' }}
                  className="pr-hide-scrollbar">
                  {active.map((t, idx) => {
                    const sm = getStatus(t.status);
                    return (
                      <button key={t.id} onClick={() => openTicketDetail(t)} style={{
                        flexShrink:0, width: active.length === 1 ? '100%' : '78%',
                        scrollSnapAlign:'start', textAlign:'right', cursor:'pointer',
                        border:'none', padding:0, background:'none',
                        animation:`pr-in 0.3s ease ${idx * 0.07}s both`,
                      }}>
                        <div style={{
                          borderRadius:'1.1rem', overflow:'hidden', position:'relative',
                          background:'var(--wt-surface)', border:`1px solid ${sm.color}40`,
                        }}>
                          <div style={{ height:2.5, background:`linear-gradient(90deg,transparent,${sm.color} 40%,rgba(56,189,248,0.6),transparent)` }}/>
                          <div style={{ position:'absolute', top:0, left:0, right:0, bottom:0, overflow:'hidden', pointerEvents:'none', borderRadius:'inherit' }}>
                            <div style={{ position:'absolute', top:0, bottom:0, width:'40%', background:'linear-gradient(90deg,transparent,rgba(255,255,255,0.04),transparent)', animation:'pr-shimmer 3.5s linear infinite', animationDelay:`${idx*0.5}s` }}/>
                          </div>
                          <div style={{ padding:'0.875rem 1rem', display:'flex', alignItems:'center', gap:'0.75rem' }}>
                            <div style={{ width:40, height:40, borderRadius:'0.875rem', flexShrink:0, position:'relative', background:sm.bg, border:`1px solid ${sm.color}40`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                              <Ticket size={18} color={sm.color} strokeWidth={1.8}/>
                              <div className="pr-status-pulse" style={{ position:'absolute', top:-3, right:-3, width:9, height:9, borderRadius:'50%', background:sm.color, border:'2px solid var(--wt-bg,#fff)' }}/>
                            </div>
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ fontSize:'0.85rem', fontWeight:900, color:T.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.subject}</div>
                              <div style={{ fontSize:'0.67rem', color:T.muted, marginTop:'0.18rem', display:'flex', alignItems:'center', gap:'0.4rem' }}>
                                <span>{t.ticket_number || ''}</span>
                                <span style={{ width:3, height:3, borderRadius:'50%', background:'var(--wt-border)', display:'inline-block' }}/>
                                <span style={{ color:sm.color, fontWeight:700 }}>{sm.label}</span>
                              </div>
                            </div>
                            {(() => { const u = getCustUnread(t); return u > 0 ? (
                              <span style={{ minWidth:20, height:20, borderRadius:999, background:'#ef4444', color:'#fff', fontSize:'0.62rem', fontWeight:900, display:'flex', alignItems:'center', justifyContent:'center', padding:'0 5px', flexShrink:0, animation:'pr-pulse 1.2s ease-in-out infinite', boxShadow:'0 0 8px rgba(239,68,68,0.5)' }}>{u}</span>
                            ) : null; })()}
                            <span className="pr-badge" style={{ fontSize:'0.65rem', fontWeight:800, color:sm.color, background:sm.bg, borderRadius:999, padding:'0.2rem 0.6rem', display:'flex', alignItems:'center', gap:'0.25rem', flexShrink:0, border:`1px solid ${sm.color}40`, animation:`pr-badge 2s ease-in-out ${idx*0.3}s infinite` }}>
                              {sm.icon}{sm.label}
                            </span>
                            <ChevronLeft size={14} style={{ color:T.muted, flexShrink:0 }}/>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Header + all tickets btn */}
          <div style={{ borderRadius:'1.1rem', padding:'1.25rem', background:'var(--wt-surface)', border:'1px solid var(--wt-border)' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1rem' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
                <div style={{ width:40, height:40, borderRadius:'0.875rem', background:'rgba(56,189,248,0.1)', border:'1px solid rgba(56,189,248,0.25)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <HelpCircle size={18} color="#38bdf8" strokeWidth={1.8}/>
                </div>
                <div>
                  <div style={{ fontSize:'1rem', fontWeight:900, color: T.text }}>التواصل مع الدعم</div>
                  <div style={{ fontSize:'0.7rem', color: T.muted }}>سنرد عليك في أقرب وقت</div>
                </div>
              </div>
              {myTickets.length > 0 && (
                <button onClick={() => setView('my-tickets')} style={{ display:'flex', alignItems:'center', gap:'0.3rem', background:'rgba(56,189,248,0.08)', border:'1px solid rgba(56,189,248,0.2)', borderRadius:'0.65rem', padding:'0.35rem 0.65rem', cursor:'pointer', color:'#38bdf8', fontSize:'0.7rem', fontWeight:800, fontFamily:"'Cairo',sans-serif" }}>
                  <List size={13}/> تذاكري
                </button>
              )}
            </div>
            <form onSubmit={sendSupport} style={{ display:'flex', flexDirection:'column', gap:'0.875rem' }}>
              <div>
                <label style={{ display:'block', fontSize:'0.7rem', fontWeight:700, color: T.muted, marginBottom:'0.35rem' }}>التصنيف</label>
                <CustomerSelect
                  value={supportForm.category}
                  onChange={val => setSupportForm(p => ({...p, category: val}))}
                  options={[
                    { value:'general', label:'استفسار عام' },
                    { value:'order',   label:'مشكلة في طلب' },
                    { value:'payment', label:'مشكلة في الدفع' },
                    { value:'account', label:'مشكلة في الحساب' },
                    { value:'other',   label:'أخرى' },
                  ]}
                />
              </div>
              <div>
                <label style={{ display:'block', fontSize:'0.7rem', fontWeight:700, color: T.muted, marginBottom:'0.35rem' }}>الموضوع <span style={{ color:'#f43f5e' }}>*</span></label>
                <input className="pr-input" type="text" value={supportForm.subject} onChange={e => setSupportForm(p => ({...p, subject: e.target.value}))} placeholder="اكتب موضوع رسالتك" required style={inputSt}/>
              </div>
              <div>
                <label style={{ display:'block', fontSize:'0.7rem', fontWeight:700, color: T.muted, marginBottom:'0.35rem' }}>الرسالة <span style={{ color:'#f43f5e' }}>*</span></label>
                <textarea className="pr-input" value={supportForm.message} onChange={e => setSupportForm(p => ({...p, message: e.target.value}))} placeholder="اشرح مشكلتك أو استفسارك بالتفصيل..." required rows={4}
                  style={{ ...inputSt, resize:'vertical', minHeight:100 }}/>
              </div>
              <button type="submit" disabled={savingSupport} style={{ ...btnPrimary, background:'linear-gradient(135deg,#38bdf8,#0891b2)', opacity: savingSupport ? 0.65 : 1, cursor: savingSupport ? 'not-allowed' : 'pointer' }}>
                {savingSupport ? <><Spinner/> جاري الإرسال...</> : <><Send size={14}/> إرسال رسالة جديدة</>}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ══ MY TICKETS VIEW ══ */}
      {view === 'my-tickets' && (
        <div style={{ display:'flex', flexDirection:'column', gap:'0.6rem', animation:'pr-in 0.25s ease' }}>
          <div style={{ fontSize:'0.72rem', fontWeight:700, color: T.muted, paddingRight:'0.25rem' }}>تذاكري ({myTickets.length})</div>
          {!loadingTickets && myTickets.length === 0 && (
            <div style={{ textAlign:'center', padding:'2rem', color: T.muted, fontSize:'0.82rem', borderRadius:'1rem', background:'var(--wt-surface)', border:'1px solid var(--wt-border)' }}>لا توجد تذاكر سابقة</div>
          )}
          {myTickets.map((t, idx) => {
            const sm = getStatus(t.status);
            return (
              <button key={t.id} onClick={() => openTicketDetail(t)} style={{
                width:'100%', background:'var(--wt-surface)', border:`1px solid ${sm.color}30`,
                borderRadius:'1rem', padding:'0.875rem 1rem', cursor:'pointer', textAlign:'right',
                display:'flex', alignItems:'center', gap:'0.75rem',
                animation:`pr-in 0.3s ease ${idx * 0.06}s both`,
                transition:'transform 0.15s ease, box-shadow 0.15s ease',
              }}>
                <div style={{ width:36, height:36, borderRadius:'0.75rem', background:'rgba(56,189,248,0.08)', border:'1px solid rgba(56,189,248,0.15)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, position:'relative' }}>
                  <Ticket size={16} color="#38bdf8" strokeWidth={1.8}/>
                  {getCustUnread(t) > 0 && (
                    <span style={{ position:'absolute', top:-5, right:-5, minWidth:16, height:16, borderRadius:999, background:'#ef4444', color:'#fff', fontSize:'0.58rem', fontWeight:900, display:'flex', alignItems:'center', justifyContent:'center', padding:'0 3px', border:'1.5px solid var(--wt-bg,#fff)', animation:'pr-pulse 1.2s ease-in-out infinite' }}>{getCustUnread(t)}</span>
                  )}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:'0.82rem', fontWeight:800, color: T.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.subject}</div>
                  <div style={{ fontSize:'0.67rem', color: T.muted, marginTop:'0.15rem', display:'flex', gap:'0.5rem' }}>
                    <span>{t.ticket_number || ''}</span>
                    {t.messages?.length > 0 && <span style={{ color:'#38bdf8' }}><MessageCircle size={10} style={{display:'inline',verticalAlign:'middle'}}/> {t.messages.length}</span>}
                  </div>
                </div>
                <span style={{ fontSize:'0.65rem', fontWeight:800, color: sm.color, background: sm.bg, borderRadius:999, padding:'0.18rem 0.55rem', display:'flex', alignItems:'center', gap:'0.25rem', flexShrink:0 }}>
                  {sm.icon}{sm.label}
                </span>
                <ChevronLeft size={13} style={{ color: T.muted, flexShrink:0 }}/>
              </button>
            );
          })}
        </div>
      )}

      {/* ══ TICKET DETAIL VIEW ══ */}
      {view === 'ticket-detail' && selectedTicket && (
        <TicketDetailView
          ticket={selectedTicket}
          apiCall={apiCall}
          toast={toast}
          onReload={async () => {
            const fresh = await apiCall(`/customer/auth/tickets/${selectedTicket.id}`);
            setSelectedTicket({ ...fresh, _from: selectedTicket._from });
          }}
          T={T}
          inputSt={inputSt}
          getStatus={getStatus}
        />
      )}

      {/* ══ DELETE VIEW ══ */}
      {view === 'delete' && (
        <div style={{ borderRadius:'1.1rem', padding:'1.25rem', background:'var(--wt-surface)', border:'1.5px solid rgba(244,63,94,0.25)', animation:'pr-in 0.25s ease' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', marginBottom:'1rem' }}>
            <div style={{ width:40, height:40, borderRadius:'0.875rem', background:'rgba(244,63,94,0.1)', border:'1px solid rgba(244,63,94,0.25)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <Trash2 size={18} color="#f43f5e" strokeWidth={1.8}/>
            </div>
            <div>
              <div style={{ fontSize:'1rem', fontWeight:900, color:'#f43f5e' }}>حذف الحساب</div>
              <div style={{ fontSize:'0.7rem', color: T.muted }}>هذا الإجراء لا يمكن التراجع عنه</div>
            </div>
          </div>
          <div style={{ padding:'0.75rem', borderRadius:'0.75rem', background:'rgba(244,63,94,0.06)', border:'1px solid rgba(244,63,94,0.15)', marginBottom:'1rem' }}>
            <p style={{ fontSize:'0.78rem', color:'rgba(244,63,94,0.85)', margin:0, lineHeight:1.6 }}>
              <AlertTriangle size={14} style={{display:'inline',marginLeft:'0.3rem',verticalAlign:'middle'}}/>{' '}
              سيتم حذف حسابك وبيانات طلباتك وعناوينك بشكل نهائي. إذا كان لديك رصيد في المحفظة، تواصل مع الدعم أولاً لاسترداده.
            </p>
          </div>
          <form onSubmit={deleteAccount} style={{ display:'flex', flexDirection:'column', gap:'0.875rem' }}>
            <div>
              <label style={{ display:'block', fontSize:'0.7rem', fontWeight:700, color: T.muted, marginBottom:'0.35rem' }}>كلمة المرور للتأكيد <span style={{ color:'#f43f5e' }}>*</span></label>
              <input className="pr-input" type="password" value={deleteForm.password} onChange={e => setDeleteForm({ password: e.target.value })} placeholder="••••••••" required dir="ltr" style={inputSt}/>
            </div>
            <button type="submit" disabled={deletingAcct} style={{ ...btnPrimary, background:'linear-gradient(135deg,#f43f5e,#dc2626)', boxShadow:'0 4px 16px rgba(244,63,94,0.3)', opacity: deletingAcct ? 0.65 : 1, cursor: deletingAcct ? 'not-allowed' : 'pointer' }}>
              {deletingAcct ? <><Spinner/> جاري الحذف...</> : <><Trash2 size={14}/> تأكيد حذف الحساب</>}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
